// frontend/src/hooks/useCanvasserCalls.ts
import { useCallback, useEffect, useRef, useState } from 'react'
import { useIsFetching, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { canvasserApi } from '../lib/api'
import {
  CacheExpiredError,
  cacheCallBatch,
  cacheCallScript,
  getCachedCallBatch,
  getCachedCallScript,
  getPendingCalls,
  queueCall,
} from '../lib/offlineStore'
import type { PendingCall } from '../lib/offlineStore'
import { isNetworkError, serverMessage, syncEngine } from '../lib/syncEngine'
import { readGrantMirror } from '../lib/grantMirror'
import type { CallBatchItemRowDto, RecordCallResult, RecordCallSubmission } from '../types/followUp'

/**
 * [CF-1 P4] The canvasser's call list. offlineFirst so a fresh query still reaches the IndexedDB copy
 * once the phone is offline; a network error reads the cached batch (7-day TTL, like walk lists).
 * Refetched on every mount: a call that drained while this screen was unmounted must not come back
 * from the in-memory cache as "not called" (the overlay starts empty), inviting a second attempt.
 */
export function useGrantCallBatch(batchId: string | undefined) {
  return useQuery({
    queryKey: ['canvasser', 'callBatch', batchId],
    queryFn: async () => {
      try {
        const batch = (await canvasserApi.getCallBatch(batchId!)).data
        await cacheCallBatch(batch)
        return batch
      } catch (err) {
        if (isNetworkError(err)) {
          const cached = await getCachedCallBatch(batchId!)
          if (cached.status === 'hit') return cached.data
          if (cached.status === 'expired') throw new CacheExpiredError()
        }
        throw err
      }
    },
    enabled: !!batchId,
    networkMode: 'offlineFirst',
    refetchOnMount: 'always',
    retry: false,
  })
}

/** One item's script (null = none resolved: 204). Cache-on-success / read-on-network-error, like useResolveScript. */
export function useCallScript(batchId: string | undefined, itemId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['canvasser', 'callScript', batchId, itemId],
    queryFn: async () => {
      try {
        const script = await canvasserApi.getCallItemScript(batchId!, itemId!)
        if (script) await cacheCallScript(batchId!, itemId!, script)
        return script
      } catch (err) {
        if (isNetworkError(err)) {
          const cached = await getCachedCallScript(batchId!, itemId!)
          if (cached.status === 'hit') return cached.data
          if (cached.status === 'expired') throw new CacheExpiredError()
        }
        throw err
      }
    },
    enabled: enabled && !!batchId && !!itemId,
    networkMode: 'offlineFirst',
    retry: false,
  })
}

/** Online only: warm the script cache for every open row that has a script, so they work offline later. */
export function usePrefetchCallScripts(batchId: string | undefined, rows: CallBatchItemRowDto[] | undefined, isOnline: boolean) {
  const qc = useQueryClient()
  const wanted = (rows ?? []).filter(r => r.status === 'Open' && r.scriptId != null && !r.doNotContact).map(r => r.itemId)
  const key = wanted.join(',')
  useEffect(() => {
    if (!batchId || !key || !isOnline) return
    for (const itemId of key.split(',')) {
      void qc.prefetchQuery({
        queryKey: ['canvasser', 'callScript', batchId, itemId],
        queryFn: async () => {
          const script = await canvasserApi.getCallItemScript(batchId, itemId)
          if (script) await cacheCallScript(batchId, itemId, script)
          return script
        },
      }).catch(() => { /* best-effort */ })
    }
    // isOnline: coming back online re-runs it, so a list first opened offline gets its scripts cached.
  }, [batchId, key, qc, isOnline])
}

/** This batch's queued calls (the "Saved on this phone, will sync" overlay). Re-read when the queue changes. */
export function usePendingCalls(batchId: string | undefined, pendingCount: number) {
  const q = useQuery({
    queryKey: ['canvasser', 'pendingCalls', batchId],
    queryFn: async (): Promise<PendingCall[]> =>
      (await getPendingCalls()).map(r => r.entry).filter(e => e.batchId === batchId),
    enabled: !!batchId,
    networkMode: 'always',
    staleTime: 0,
  })
  const refetch = q.refetch
  useEffect(() => { void refetch() }, [pendingCount, refetch])
  return q.data ?? []
}

/** One row's local call: still queued (`synced: false`) or synced but not yet reflected by the server read. */
export interface LocalCall { entry: PendingCall; synced: boolean }

/** The longest a drained call is held as `synced` without the server read confirming it. */
export const CALL_HOLD_MAX_MS = 30_000

/**
 * The per-item overlay for the call list. A queued call shows until it drains; once the drain removes it,
 * the batch (and Today's counts) are refetched, and the call stays shown as `synced` until the server row
 * reflects it, so the row never flashes back to "not called" and invites a second call with a new
 * clientCallId. Released when the row's attemptCount grows, when a batch read that landed after the drain
 * shows the call's outcome (the baseline may already have included the call, so the count never grows),
 * or after CALL_HOLD_MAX_MS at the latest.
 */
export function useCallOverlay(
  batchId: string, pending: PendingCall[], rows: CallBatchItemRowDto[] | undefined,
): Map<string, LocalCall> {
  const qc = useQueryClient()
  const prev = useRef<PendingCall[]>([])
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const [settled, setSettled] = useState<Map<string, { entry: PendingCall; attemptCount: number; at: number }>>(() => new Map())
  // Re-render when a batch refetch settles, even one whose data is structurally identical (same reference).
  useIsFetching({ queryKey: ['canvasser', 'callBatch', batchId] })
  const readAt = qc.getQueryState(['canvasser', 'callBatch', batchId])?.dataUpdatedAt ?? 0

  useEffect(() => {
    const now = new Set(pending.map(p => p.call.clientCallId))
    // Only a successful drain removes an entry (rejected ones are kept), so a vanished entry was saved.
    const gone = prev.current.filter(p => !p.rejected && !now.has(p.call.clientCallId))
    prev.current = pending
    if (gone.length === 0) return
    const at = Date.now()
    setSettled(cur => {
      const next = new Map(cur)
      for (const g of gone) {
        const row = rowsRef.current?.find(r => r.itemId === g.call.itemId)
        next.set(g.call.itemId, { entry: g, attemptCount: row?.attemptCount ?? 0, at })
      }
      return next
    })
    void qc.invalidateQueries({ queryKey: ['canvasser', 'callBatch', batchId] })
    void qc.invalidateQueries({ queryKey: ['canvasser', 'today'] })
  }, [pending, batchId, qc])

  // Bounded hold: drop the oldest entry once it has waited CALL_HOLD_MAX_MS.
  useEffect(() => {
    if (settled.size === 0) return
    const oldest = Math.min(...[...settled.values()].map(s => s.at))
    const t = setTimeout(() => setSettled(cur => {
      const now = Date.now()
      const next = new Map([...cur].filter(([, s]) => now - s.at < CALL_HOLD_MAX_MS))
      return next.size === cur.size ? cur : next
    }), Math.max(0, oldest + CALL_HOLD_MAX_MS - Date.now()))
    return () => clearTimeout(t)
  }, [settled])

  const out = new Map<string, LocalCall>()
  for (const [itemId, s] of settled) {
    const row = rows?.find(r => r.itemId === itemId)
    const grew = !!row && row.attemptCount > s.attemptCount
    const confirmed = !!row && readAt > s.at && row.calledAt != null && row.lastOutcome === s.entry.call.outcome
    if (!grew && !confirmed) out.set(itemId, { entry: s.entry, synced: true })
  }
  for (const p of pending) out.set(p.call.itemId, { entry: p, synced: false })
  return out
}

export type RecordCallStatus = 'saved' | 'queued' | 'rejected'
export interface RecordCallOutcome { status: RecordCallStatus; message: string | null }

/** The single-call endpoint answers an all-rejected request with 400; per-item results may ride on it. */
function resultFromRejection(err: unknown): RecordCallResult | null {
  const res = (err as { response?: { status?: number; data?: { results?: unknown } } } | null)?.response
  return res?.status === 400 && Array.isArray(res.data?.results) ? ((res.data!.results as RecordCallResult[])[0] ?? null) : null
}

function is400(err: unknown): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === 400
}

/**
 * Online: POST a single-call batch. Saved -> 'saved'; a terminal rejection -> 'rejected' with the
 * server's own message (never queued); anything retryable (network, 5xx, Success = false) is queued
 * with its ORIGINAL clientCallId and stamped with the recording grant (R4). Offline: queued.
 */
export async function submitCall(batchId: string, call: RecordCallSubmission): Promise<RecordCallOutcome> {
  if (navigator.onLine) {
    let r: RecordCallResult | null = null
    try {
      // [CF-1 P5] Name the batch, so a chase item in several of the grant's batches marks THIS one's row.
      r = (await canvasserApi.recordCalls([{ ...call, callBatchId: batchId }])).data.results[0] ?? null
    } catch (err) {
      r = resultFromRejection(err)
      if (!r && is400(err)) return { status: 'rejected', message: serverMessage(err, 'This call could not be saved') }
    }
    if (r?.success) return { status: 'saved', message: null }
    if (r?.rejected) return { status: 'rejected', message: r.error ?? 'This call could not be saved' }
    // Anything else (network, 5xx, Success = false) is retryable: falls through to the queue.
  }
  await queueCall(call, { batchId, grantId: readGrantMirror()?.grantId })
  await syncEngine.refreshPendingCount()
  return { status: 'queued', message: null }
}

export function callToast(o: RecordCallOutcome): { type: 'success' | 'info' | 'error'; title: string; description?: string } {
  if (o.status === 'rejected') return { type: 'error', title: "This call couldn't be saved", description: o.message ?? undefined }
  if (o.status === 'queued') return { type: 'info', title: 'Saved on this phone, will sync' }
  return { type: 'success', title: 'Saved' }
}

export function useRecordCall() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ batchId, call }: { batchId: string; call: RecordCallSubmission }) => submitCall(batchId, call),
    // The function handles offline itself (queue); TanStack's default 'online' would PAUSE it offline.
    networkMode: 'always',
    onSuccess: (o, { batchId }) => {
      void qc.invalidateQueries({ queryKey: ['canvasser', 'pendingCalls', batchId] })
      if (o.status === 'saved') void qc.invalidateQueries({ queryKey: ['canvasser', 'callBatch', batchId] })
    },
  })
  // useMutation returns a fresh object every render: depend on the stable mutateAsync only.
  const run = mutation.mutateAsync
  const record = useCallback(
    (batchId: string, call: RecordCallSubmission) => run({ batchId, call }), [run])
  return { record }
}
